import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { useParams } from "react-router-dom"
import { withTranslation } from "react-i18next"
import { bulletinSanitizeSettings, checkIfValidUUID, getLocaleText, getLottieLoadingOptions, getLottieNothingFound } from "utils/general"
import { Card, CardBody, Col, Container, Row } from "reactstrap"
import Lottie from "react-lottie"
import { getReleaseNote } from "helpers/database_helper"
import moment from "moment"
import suvantoLogo from "assets/images/suvanto/Suvanto_logo2_transparent.png"
import LanguageDropdown from "components/CommonForBoth/TopbarDropdown/LanguageDropdown"
import sanitizeHtml from 'sanitize-html';

const ReleaseNote = props => {

  const [release, setRelease] = useState(null)
  const [loading, setLoading] = useState(true)
  const [language, setLanguage] = useState()
  const [whatChanges, setWhatChanges] = useState()
  const [useChanges, setUseChanges] = useState()
  const [affects, setAffects] = useState()

  const params = useParams()
  const abortRef = useRef(null)
  useEffect(() => {
    document.title = props.t("Release note") + " | Suvanto Care"
    document.body.dataset.style = 0
    abortRef.current = new AbortController()

    // Local storage listener for lang change
    setLanguage(getLocaleText() ?? "en")
    globalThis.addEventListener("storage", () => {
      let newLang = getLocaleText() ?? "en"
      if (newLang != language) {
        setLanguage(newLang)
      }
    })

    // Check if valid params
    if (params?.id != undefined && checkIfValidUUID(params.id)) {
      getRelease(params.id)
    }
    else {
      setLoading(false)
    }

    return () => abortRef.current.abort()
  }, [])

  useEffect(() => {
    if (release) {
      localizeContent()
    }
  }, [release, language])

  // Get release note
  const getRelease = (id) => {
    setLoading(true)
    getReleaseNote(abortRef.current, id).then(result => {
      if (result.length == 1 && result[0].guid == params.id) {

        if (result[0].release_date) result[0].release_date = moment(result[0].release_date).format("DD.MM.YYYY")
        if (result[0].informed_date) result[0].informed_date = moment(result[0].informed_date).format("DD.MM.YYYY")
        if (result[0].updated) result[0].updated = moment(result[0].updated).format("DD.MM.YYYY HH:mm")

        setRelease(result[0])
      }
    }).catch(err => {
      console.log(err)
    }).finally(() => {
      setLoading(false)
    })
  }

  // Localize note content
  const localizeContent = () => {
    let rawWhatChanges = release.what_changes_fi
    let rawUseChanges = release.how_use_changes_fi
    let rawAffects = release.how_effects_fi
    switch (language) {
      case "en":
        if (release.what_changes_en) rawWhatChanges = release.what_changes_en
        if (release.how_use_changes_en) rawUseChanges = release.how_use_changes_en
        if (release.how_effects_en) rawAffects = release.how_effects_en
        break;
      case "sv":
        if (release.what_changes_sv) rawWhatChanges = release.what_changes_sv
        if (release.how_use_changes_sv) rawUseChanges = release.how_use_changes_sv
        if (release.how_effects_sv) rawAffects = release.how_effects_sv
        break;
    }

    setWhatChanges(sanitizeHtml(rawWhatChanges, bulletinSanitizeSettings))
    setUseChanges(sanitizeHtml(rawUseChanges, bulletinSanitizeSettings))
    setAffects(sanitizeHtml(rawAffects, bulletinSanitizeSettings))
  }

  // Get matching target name
  const getTargetName = id => {
    switch (id) {
      case 1:
      case 2:
      case 3:
      case 4:
      case 5:
        return ("ReleaseNoteTypes." + id)
      default:
        return "Unknown"
    }
  }

  return (
    <>
      <div className="page-content">
        <Container>
          <Row className="justify-content-center">
            <Col lg={12} xl={10} xxl={9}>

              {loading && <>
                <Lottie
                  options={getLottieLoadingOptions()}
                  height={100}
                  width={100}
                />
              </>}

              {!loading && !release && <>
                <Card style={{ maxWidth: "1000px" }}>
                  <CardBody>
                    <div className="text-center mb-4">
                      <Lottie
                        options={getLottieNothingFound()}
                        height={150}
                        width={150}
                      />
                      <h5 className="text-muted mt-2">
                        {props.t("No notice found")}
                      </h5>
                    </div>
                  </CardBody>
                </Card>
              </>}

              {!loading && release && <>
                <LanguageDropdown
                  showText={true}
                  onChange={(lan) => {
                    let newLang = getLocaleText() ?? "en"
                    if (newLang != language) {
                      setLanguage(newLang)
                    }
                  }}
                />
                <Card style={{}}>
                  <CardBody className="p-5">

                    <h3 className="text-primary">{props.t("Release note")}</h3>
                    <h6>{release.informed_date}</h6>
                    {release.updated &&
                      <span>{props.t("Updated") + " " + release.updated}</span>
                    }

                    <img
                      className="d-none d-md-block"
                      src={suvantoLogo}
                      alt=""
                      style={{
                        position: "absolute",
                        top: "24px",
                        right: "24px",
                        height: "120px",

                      }}
                    ></img>

                    <h5 style={{ marginTop: "30px" }} className="">{props.t(getTargetName(release.target))}</h5>
                    <span>{(release.type == 1 ? props.t("Hotfix") : props.t("Update")) + " v." + release.version}</span>

                    <h5 style={{ marginTop: "30px", marginBottom: "12px" }}>{props.t("Changes")}</h5>
                    <div className="bulletin-content" style={{ whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: whatChanges }} />

                    <h5 style={{ marginTop: "30px", marginBottom: "12px" }} className="">{props.t("Visible in")}</h5>
                    <div className="bulletin-content" style={{ whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: useChanges }} />

                    <h5 style={{ marginTop: "30px" }} className="">{props.t("Affects")}</h5>
                    <table className="table" style={{ maxWidth: "500px" }} role="presentation">
                      <tbody>
                        <tr>
                          <td>{props.t("User interface for end user")}</td>
                          <td>{release.changes_enduser_ui == 0 ? props.t("No") : props.t("Yes")}</td>
                        </tr>
                        <tr>
                          <td>
                            {props.t("User interface for professional user")}
                          </td>
                          <td>{release.changes_pro_ui == 0 ? props.t("No") : props.t("Yes")}</td>
                        </tr>
                        <tr>
                          <td>{props.t("Procedural changes")}</td>
                          <td>{release.changes_user_methods == 0 ? props.t("No") : props.t("Yes")}</td>
                        </tr>
                      </tbody>
                    </table>
                    <div className="bulletin-content" style={{ whiteSpace: "pre-line" }} dangerouslySetInnerHTML={{ __html: affects }} />

                    <h5 style={{ marginTop: "30px" }} className="">{props.t("Release date")}</h5>
                    <span>{release.release_date}</span>
                  </CardBody>
                </Card>

                <div className="mt-5 text-center">
                  <a href="https://www.suvantocare.fi/" target="_blank" rel="noreferrer">
                    <p className="text-primary">
                      © {new Date().getFullYear()} Suvanto Care
                    </p>
                  </a>
                </div>

              </>}
            </Col>
          </Row>

        </Container>
      </div>
    </>
  )
}

ReleaseNote.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(ReleaseNote)